import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useResetPassword } from "@/features/auth/api/useAuth";
import {
  PASSWORD_MIN_LENGTH,
  resetPasswordSchema,
  type ResetPasswordValues,
} from "@/features/auth/lib/schemas";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";

export function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const { token } = useSearch({ from: "/public/reset-password" });
  const reset = useResetPassword();
  const errorMessage = useApiErrorMessage();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordValues>({ resolver: zodResolver(resetPasswordSchema) });

  const onSubmit = handleSubmit((values) => {
    reset.mutate({ token: token ?? "", new_password: values.new_password });
  });

  if (token === undefined) {
    return (
      <Card className="mx-auto max-w-md">
        <h1 className="mb-4 text-2xl font-bold tracking-tight">{t("resetPassword.heading")}</h1>
        <Alert tone="error">{t("resetPassword.missingToken")}</Alert>
      </Card>
    );
  }

  if (reset.isSuccess) {
    return (
      <Card className="mx-auto max-w-md">
        <h1 className="mb-4 text-2xl font-bold tracking-tight">{t("resetPassword.heading")}</h1>
        {/* Toutes les sessions ont été révoquées côté serveur : l'utilisateur
            doit se reconnecter, y compris là où il était déjà connecté. */}
        <Alert tone="success">{t("resetPassword.success")}</Alert>
        <p className="mt-6 text-sm">
          <Link to="/login" className="text-brand-600 hover:underline">
            {t("resetPassword.goToLogin")}
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <Card className="mx-auto max-w-md">
      <h1 className="mb-6 text-2xl font-bold tracking-tight">{t("resetPassword.heading")}</h1>

      {reset.isError && (
        <Alert tone="error" className="mb-4">
          {errorMessage(reset.error)}
        </Alert>
      )}

      <form
        onSubmit={(event) => {
          // `handleSubmit` rend toujours une fonction asynchrone, même sur un
          // gestionnaire synchrone : `void` dit explicitement qu'on n'attend
          // pas la promesse, ce que React ne saurait pas faire de toute façon.
          void onSubmit(event);
        }}
        className="space-y-4"
        noValidate
      >
        <Field
          label={t("fields.newPassword")}
          type="password"
          autoComplete="new-password"
          hint={t("validation.passwordHint", { count: PASSWORD_MIN_LENGTH })}
          error={
            errors.new_password && t("validation.passwordTooShort", { count: PASSWORD_MIN_LENGTH })
          }
          {...register("new_password")}
        />
        <Button type="submit" className="w-full" disabled={reset.isPending}>
          {reset.isPending ? t("resetPassword.submitting") : t("resetPassword.submit")}
        </Button>
      </form>
    </Card>
  );
}
